import {
  FlightDashboardHeader,
  FlightSummarySection,
  FlightTrendSection,
  FlightRevenueSection,
  FlightPerformanceSection,
  FlightPostTicketingSection,
  getFlightRevenueByClassChartData,
} from "../../../components/reportDashboard/flights/flights";
import {
  useFlightPostTicketingFees,
  useFlightRevenueStreams,
  useFlightRevenueTrends,
  useFlightSummaryMetrics,
  useFlightTopRoutes,
  useReportDateRange,
  useRevenueByClass,
  useTopAirlines,
} from "../../../hooks/useReportDashboard";
import { mapRevenueToMonths } from "../../../utils/reportDashboard/reportDashboardUtils";
import { getErrorMessage } from "../../../utils/error";

export default function Flights({ onViewChange } = {}) {
  const { dateRange, onDateRangeChange } = useReportDateRange();

  const {
    data: flightSummaryData,
    isLoading: flightSummaryLoading,
    isError: flightSummaryError,
    error: flightSummaryErrObj,
    refetch: flightSummaryRefetch,
  } = useFlightSummaryMetrics(dateRange);

  const {
    data: flightTrendsData,
    isLoading: flightTrendsLoading,
    isError: flightTrendsError,
    error: flightTrendsErrObj,
    refetch: flightTrendsRefetch,
  } = useFlightRevenueTrends(dateRange);

  const {
    data: flightStreamsData,
    isLoading: flightStreamsLoading,
    isError: flightStreamsError,
    error: flightStreamsErrObj,
    refetch: flightStreamsRefetch,
  } = useFlightRevenueStreams(dateRange);

  const {
    data: flightTopRoutesData,
    isLoading: flightTopRoutesLoading,
    isError: flightTopRoutesError,
    error: flightTopRoutesErrObj,
    refetch: flightTopRoutesRefetch,
  } = useFlightTopRoutes(dateRange);

  const {
    data: flightFeesData,
    isLoading: flightFeesLoading,
    isError: flightFeesError,
    error: flightFeesErrObj,
    refetch: flightFeesRefetch,
  } = useFlightPostTicketingFees(dateRange);

  const {
    data: revenueByClassData,
    isLoading: revenueByClassLoading,
    isError: revenueByClassError,
    error: revenueByClassErrObj,
    refetch: revenueByClassRefetch,
  } = useRevenueByClass(dateRange);

  const {
    data: topAirlinesData,
    isLoading: topAirlinesLoading,
    isError: topAirlinesError,
    error: topAirlinesErrObj,
    refetch: topAirlinesRefetch,
  } = useTopAirlines(dateRange);

  const summaryState = {
    loading: flightSummaryLoading,
    error: flightSummaryError,
    message:
      getErrorMessage(flightSummaryErrObj) ||
      "Failed to load flight summary metrics",
    onRetry: flightSummaryRefetch,
  };

  const trendsState = {
    loading: flightTrendsLoading,
    error: flightTrendsError,
    message:
      getErrorMessage(flightTrendsErrObj) || "Failed to load revenue trends",
    onRetry: flightTrendsRefetch,
  };

  const streamsState = {
    loading: flightStreamsLoading,
    error: flightStreamsError,
    message:
      getErrorMessage(flightStreamsErrObj) || "Failed to load revenue streams",
    onRetry: flightStreamsRefetch,
  };

  const routesState = {
    loading: flightTopRoutesLoading,
    error: flightTopRoutesError,
    message: getErrorMessage(flightTopRoutesErrObj) || "Failed to load top routes",
    onRetry: flightTopRoutesRefetch,
  };

  const feesState = {
    loading: flightFeesLoading,
    error: flightFeesError,
    message:
      getErrorMessage(flightFeesErrObj) || "Error loading flight fees data.",
    onRetry: flightFeesRefetch,
  };

  const classState = {
    loading: revenueByClassLoading,
    error: revenueByClassError,
    message:
      getErrorMessage(revenueByClassErrObj) || "Failed to load class revenue",
    onRetry: revenueByClassRefetch,
  };

  const airlinesState = {
    loading: topAirlinesLoading,
    error: topAirlinesError,
    message: getErrorMessage(topAirlinesErrObj) || "Failed to load airlines",
    onRetry: topAirlinesRefetch,
  };

  const summaryMetrics = Object.values(flightSummaryData?.data || {});
  const trendData = flightTrendsData?.data?.datasets
    ? mapRevenueToMonths(flightTrendsData.data.datasets)
    : [];
  const revenueChartData = getFlightRevenueByClassChartData(revenueByClassData);
  const revenueStreams = flightStreamsData?.data || [];
  const topAirlines = topAirlinesData?.data || [];
  const topRoutes = flightTopRoutesData?.data || [];
  const feesData = flightFeesData?.data;

  return (
    <div className="space-y-8 p-1">
      <FlightDashboardHeader onDateRangeChange={onDateRangeChange} />

      <FlightSummarySection
        state={summaryState}
        summaryMetrics={summaryMetrics}
      />

      <FlightTrendSection state={trendsState} trendData={trendData} />

      <FlightRevenueSection
        classState={classState}
        streamsState={streamsState}
        revenueChartData={revenueChartData}
        revenueStreams={revenueStreams}
      />

      <FlightPerformanceSection
        airlinesState={airlinesState}
        routesState={routesState}
        topAirlines={topAirlines}
        topRoutes={topRoutes}
      />

      <FlightPostTicketingSection
        state={feesState}
        feesData={feesData}
        onViewChange={onViewChange}
      />
    </div>
  );
}
